import React, { useContext, useMemo, useState } from 'react';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import type { TagCondition, HubTagTemplate } from 'dorian-shared/types/branch/HubOption';
import { HUB_MAX_TAG_CONDITIONS } from 'dorian-shared/types/branch/HubOption';
import {
  MemoryDTO,
  MemoryType,
} from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { NodeEditContext } from '../../../../context/NodeEditContext';
import { TagConditionCard } from './TagConditionCard';
import { isTagConditionValid } from './tagConditionValidation';
import './TagsOverrides.scss';

const MOUSE_SENSOR_OPTIONS = { activationConstraint: { distance: 5 } };
const KEYBOARD_SENSOR_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };

interface TagsOverridesProps {
  optionUuid: string;
  conditions: TagCondition[];
  templates: HubTagTemplate[];
  isDisabled: boolean;
  onDragEnd: (event: DragEndEvent) => void;
  onAdd: (tagTemplateId: number) => void;
  onRemove: (uuid: string) => void;
  onUpdate: (uuid: string, updated: TagCondition) => void;
}

export function TagsOverrides({
  optionUuid,
  conditions,
  templates,
  isDisabled,
  onDragEnd,
  onAdd,
  onRemove,
  onUpdate,
}: TagsOverridesProps) {
  const ctx = useContext(NodeEditContext) as Record<string, unknown> | undefined;
  const memoryBankSlots = (ctx?.memoryBankSlots as MemoryDTO[] | undefined) ?? [];
  const memoryVariables = useMemo(
    () => memoryBankSlots.filter((s: MemoryDTO) => s.type === MemoryType.Number),
    [memoryBankSlots],
  );

  const sortableItems = useMemo(
    () => conditions.map((c) => c.uuid),
    [conditions],
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const sensors = useSensors(
    useSensor(MouseSensor, MOUSE_SENSOR_OPTIONS),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS),
  );

  const handleAdd = () => {
    const id = Number(selectedTemplateId);
    if (id > 0) {
      onAdd(id);
      setSelectedTemplateId('');
    }
  };

  const atLimit = conditions.length >= HUB_MAX_TAG_CONDITIONS;

  return (
    <div className="tags-overrides" data-testid="tags-overrides">
      <p className="tags-overrides__helper">
        These tags are shown only when their override condition is met.
      </p>

      <DndContext
        id={`tags-dnd-${optionUuid}`}
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={onDragEnd}
      >
        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
          <div className="list-group mb-1">
            {conditions.map((cond) => (
              <TagConditionCard
                key={cond.uuid}
                condition={cond}
                template={templates.find((t) => t.id === cond.tagTemplateId)}
                memoryVariables={memoryVariables}
                isDisabled={isDisabled}
                isValid={isTagConditionValid(cond)}
                onChange={(updated) => onUpdate(cond.uuid, updated)}
                onRemove={() => onRemove(cond.uuid)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Otherwise row */}
      <div className="tags-overrides__otherwise">
        <strong className="small mr-2">Otherwise</strong>
        <small className="text-muted">uses default tags above</small>
      </div>

      {!atLimit && templates.length > 0 && (
        <div className="d-flex align-items-center mt-1">
          <select
            className="form-control form-control-sm mr-1"
            style={{ width: 200 }}
            value={selectedTemplateId}
            disabled={isDisabled}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
          >
            <option value="">+ Add override...</option>
            {templates.map((t) => (
              <option key={t.id} value={String(t.id)}>
                {t.name}{t.text ? ` (${t.text})` : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm ml-1"
            disabled={isDisabled || !selectedTemplateId}
            onClick={handleAdd}
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}
